import React, { useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useThemeMode } from '../../design-system/theme';
import useABTest from '../../hooks/useABTest';
import { isPreviewExperimentOverrideHost } from '../../utils/experimentOverrideHosts';
import {
  EducationDesignPreview,
  normalizeEducationDesignVariant,
} from '../education/EducationDesignExperiment';
import NextChallengePage from './NextChallengePage';
import {
  CHALLENGE_CLARITY_HOME_EXPERIMENT,
  type ChallengeClarityHomeVariant,
  type ChallengeExperimentContext,
} from './challengePageExperiment';
import './ChallengePageClarityHome.css';

// The JavaScript hook exposes this stable contract; its legacy JSDoc returns Object.
// Keep the boundary explicit, as in EducationDesignExperiment.
type ChallengeAssignment = {
  variant: string | boolean | null;
  isLoading: boolean;
  isExperimentActive: boolean;
  isPreviewOverride: boolean;
};

const normalizeDesign = (value: string | boolean | null): ChallengeClarityHomeVariant | null =>
  value === 'control' || value === 'test' || value === 'form_first' ? value : null;

/** A new assignment replaces the old design wrapper atomically, never its history. */
export default function ChallengeClarityHomeExperiment({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const location = useLocation();
  const [theme] = useThemeMode();
  const experiment = CHALLENGE_CLARITY_HOME_EXPERIMENT;
  const isTarget = location.pathname.replace(/\/+$/, '') === '/challenges';
  const previewHost = isTarget && isPreviewExperimentOverrideHost();
  const searchParams = new URLSearchParams(location.search);
  const preview = previewHost ? normalizeDesign(searchParams.get('challengeDesign')) : null;
  // An explicit design preview of the existing experiment (?educationDesign=)
  // goes straight to that route. It never waits on this flag.
  const existingRoutePreview =
    previewHost && !preview && Boolean(searchParams.get('educationDesign'));
  const trackingData = useMemo(
    () => ({
      page: location.pathname,
      challenge_path: location.pathname,
      surface: 'challenge_landing_page',
      education_theme: theme,
      validVariants: experiment.posthogVariants,
      skipFeatureFlagEvaluation: !isTarget || Boolean(preview) || existingRoutePreview,
      forcedVariant: !isTarget || existingRoutePreview ? 'control' : preview || undefined,
      trackForcedExposure: false,
    }),
    [location.pathname, theme, experiment.posthogVariants, isTarget, preview, existingRoutePreview],
  );
  // SAFETY: useABTest returns these four fields on every path; the JS hook
  // normalizes variants to string, boolean, or null and supplies boolean state.
  const { variant, isLoading, isExperimentActive, isPreviewOverride } = useABTest(
    experiment.name,
    experiment.featureFlag,
    'control',
    trackingData,
  ) as ChallengeAssignment;
  // True when this page first rendered before PostHog answered. That visitor
  // has had a usable page from the start and keeps it (see below).
  const renderedWhileResolving = useRef(isLoading && !preview).current;
  if (!isTarget || existingRoutePreview) return <>{children}</>;
  // Rob, 2026-09-23: "The register button should be ready from the moment the
  // page loads." While PostHog answers, which can take seconds on a slow phone
  // or behind an ad blocker, render the original page with a working form, not
  // a loading screen. It is the same tree every arm below renders, so when the
  // assignment lands on A, B or C the page component, and what the visitor
  // typed, stays. The old experiment is still never mounted before this one
  // resolves. If this experiment then answers "not running", the visitor keeps
  // the original page they already have rather than having it swapped for the
  // existing route (whose flag, 459166, is off, so it shows the same design).
  const resolving = isLoading && !preview;
  const assigned = preview || (isExperimentActive ? normalizeDesign(variant) : null);
  if (!assigned && !resolving && !renderedWhileResolving) return <>{children}</>;
  // A control preview on localhost or Dev still has to honor educationDesign.
  // Live control keeps the replacement page. Clarity Home stays the test arm.
  const educationDesignPreview =
    preview === 'control'
      ? normalizeEducationDesignVariant(searchParams.get('educationDesign'))
      : null;
  if (educationDesignPreview) return <>{children}</>;
  const design: ChallengeClarityHomeVariant = assigned || 'control';
  const active = Boolean(assigned && isExperimentActive && !preview && !isPreviewOverride);
  const featureProperty = `$feature/${experiment.featureFlag}` as const;
  const experimentContext: ChallengeExperimentContext | undefined = active
    ? {
        experiment_name: experiment.name,
        experiment_variant: design,
        feature_flag: experiment.featureFlag,
        [featureProperty]: design,
        is_experiment_active: true,
      }
    : undefined;
  return (
    <div
      className={`education-design-experiment${design === 'test' ? ' challenge-home-review' : ''}`}
      data-testid="challenge-clarity-home-experiment"
      data-challenge-design={design}
      data-assignment={resolving ? 'resolving' : 'resolved'}
      data-experiment-active={String(active)}
      data-education-theme={theme}
      data-bs-theme={theme}
    >
      <EducationDesignPreview variant={design === 'form_first' ? 'form_first' : 'control'}>
        <NextChallengePage
          presentation={design === 'test' ? 'clarity-home' : undefined}
          experimentContext={experimentContext}
        />
      </EducationDesignPreview>
    </div>
  );
}
